// src/components/macro/EconomicCalendarView.tsx
// Compact calendar for economic events - reusable across main view and widget

import React, { useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";
import { useEconomicCalendarEvents } from "@/hooks/macro/useEconomicCalendarEvents";
import type { EconomicCalendarEvent } from "@/types/dashboards/macro";

interface EconomicCalendarViewProps {
  /** Accent color theme */
  accentColor?: 'emerald' | 'purple';
  /** Maximum number of events to show per date group (widget uses fewer) */
  maxEventsPerDate?: number;
  /** Maximum number of date groups to show */
  maxDateGroups?: number;
  /** Compact mode for widget (smaller text, tighter spacing) */
  compact?: boolean;
  /** Whether to show the header with title and toggle (false when parent provides header) */
  showHeader?: boolean;
  /** Controlled view mode (optional - uses internal state if not provided) */
  viewMode?: 'upcoming' | 'recent';
  /** Callback when view mode changes (for controlled mode) */
  onViewModeChange?: (mode: 'upcoming' | 'recent') => void;
}

const EconomicCalendarView: React.FC<EconomicCalendarViewProps> = ({
  accentColor = 'emerald',
  maxEventsPerDate,
  maxDateGroups,
  compact = false,
  showHeader = true,
  viewMode: controlledViewMode,
  onViewModeChange,
}) => {
  const [internalViewMode, setInternalViewMode] = useState<'upcoming' | 'recent'>('upcoming');

  // Use controlled or uncontrolled mode
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = onViewModeChange ?? setInternalViewMode;

  // Calculate all date-related values once
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0]!;

  // Calculate date range: 1 month past to 1 month future
  const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  const oneMonthFromNow = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());

  const startDate = oneMonthAgo.toISOString().split('T')[0]!;
  const endDate = oneMonthFromNow.toISOString().split('T')[0]!;

  // Fetch economic calendar events
  const { data: events, isLoading, error } = useEconomicCalendarEvents({
    startDate,
    endDate,
  });

  // Filter, sort, and group events by date
  const groupedEvents = useMemo(() => {
    if (!events) return new Map<string, EconomicCalendarEvent[]>();

    const filtered = events.filter((event: EconomicCalendarEvent) => {
      const eventDate = event.event_date;
      if (!eventDate) return false;

      if (viewMode === 'upcoming') {
        return eventDate >= todayStr;
      } else {
        return eventDate < todayStr;
      }
    });

    // Sort
    const sorted = filtered.sort((a, b) => {
      const dateA = a.event_date || '';
      const dateB = b.event_date || '';
      return viewMode === 'upcoming'
        ? dateA.localeCompare(dateB)
        : dateB.localeCompare(dateA);
    });

    // Group by date
    const groups = new Map<string, EconomicCalendarEvent[]>();
    sorted.forEach((event) => {
      const date = event.event_date;
      if (!groups.has(date)) {
        groups.set(date, []);
      }
      groups.get(date)!.push(event);
    });

    return groups;
  }, [events, viewMode, todayStr]);

  const formatDateHeader = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number) as [number, number, number];
    const date = new Date(year, month - 1, day);
    const isToday = dateStr === todayStr;
    const isTomorrow = (() => {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return dateStr === tomorrow.toISOString().split('T')[0];
    })();

    if (isToday) return 'Today';
    if (isTomorrow) return 'Tomorrow';

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatValue = (val: number | null) => {
    if (val === null) return '—';
    if (Math.abs(val) >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
    if (Math.abs(val) >= 1000) return `${(val / 1000).toFixed(1)}K`;
    return val.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  // Get color for actual value based on comparison with forecast
  const getActualColor = (actual: number | null, forecast: number | null) => {
    if (actual === null || forecast === null) return { label: 'text-slate-400', value: 'text-slate-300' };
    if (actual > forecast) return { label: 'text-emerald-400/70', value: 'text-emerald-400' };
    if (actual < forecast) return { label: 'text-rose-400/70', value: 'text-rose-400' };
    return { label: 'text-slate-400', value: 'text-slate-300' }; // Equal
  };

  const totalEvents = Array.from(groupedEvents.values()).flat().length;

  // Accent color styles
  const accent = accentColor === 'purple' ? {
    activeBg: 'bg-purple-500/80',
    todayBg: 'bg-purple-500/10',
    todayBorder: 'border-purple-500/30',
    todayHeaderBg: 'bg-purple-500/20',
    todayHeaderBorder: 'border-purple-500/20',
    todayText: 'text-purple-300',
    todayDot: 'bg-purple-400',
  } : {
    activeBg: 'bg-emerald-500/80',
    todayBg: 'bg-emerald-500/10',
    todayBorder: 'border-emerald-500/30',
    todayHeaderBg: 'bg-emerald-500/20',
    todayHeaderBorder: 'border-emerald-500/20',
    todayText: 'text-emerald-300',
    todayDot: 'bg-emerald-400',
  };

  // Size styles based on compact mode
  const sizes = compact ? {
    toggleText: 'text-[10px]',
    togglePadding: 'px-2 py-0.5',
    dateText: 'text-[11px]',
    eventText: 'text-[11px]',
    valueLabel: 'text-[9px]',
    valueText: 'text-[10px]',
    countText: 'text-[10px]',
    footerText: 'text-[10px]',
    eventPadding: 'px-2.5 py-1.5',
    headerPadding: 'px-2.5 py-1.5',
    dotSize: 'w-1.5 h-1.5',
  } : {
    toggleText: 'text-[11px]',
    togglePadding: 'px-2 py-1',
    dateText: 'text-sm',
    eventText: 'text-[13px]',
    valueLabel: 'text-[10px]',
    valueText: 'text-[11px]',
    countText: 'text-[11px]',
    footerText: 'text-[11px]',
    eventPadding: 'px-3 py-2',
    headerPadding: 'px-3 py-2',
    dotSize: 'w-2 h-2',
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="text-center py-4 text-sm text-red-400">
        Error loading calendar: {error.message}
      </div>
    );
  }

  // Limit date groups if specified
  const displayedEntries = maxDateGroups
    ? Array.from(groupedEvents.entries()).slice(0, maxDateGroups)
    : Array.from(groupedEvents.entries());

  // Toggle component - used in header or standalone
  const Toggle = (
    <div className="flex items-center bg-slate-800/60 rounded-md p-0.5 flex-shrink-0">
      <button
        onClick={() => setViewMode('upcoming')}
        className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors whitespace-nowrap ${
          viewMode === 'upcoming'
            ? `${accent.activeBg} text-white`
            : 'text-slate-400 hover:text-white'
        }`}
      >
        Upcoming
      </button>
      <button
        onClick={() => setViewMode('recent')}
        className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors whitespace-nowrap ${
          viewMode === 'recent'
            ? `${accent.activeBg} text-white`
            : 'text-slate-400 hover:text-white'
        }`}
      >
        Recent
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header with toggle at top right */}
      {showHeader && (
        <div className={`flex items-center justify-end ${compact ? 'mb-2' : 'mb-3'} flex-shrink-0`}>
          {Toggle}
        </div>
      )}

      {/* Events List */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {totalEvents === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <div className="text-sm">
              No {viewMode === 'upcoming' ? 'upcoming' : 'recent'} events
            </div>
          </div>
        ) : (
          <div className={compact ? 'space-y-2' : 'space-y-2'}>
            {displayedEntries.map(([date, dateEvents]) => {
              const isToday = date === todayStr;
              const visibleEvents = maxEventsPerDate
                ? dateEvents.slice(0, maxEventsPerDate)
                : dateEvents;
              const hiddenCount = dateEvents.length - visibleEvents.length;

              return (
                <div
                  key={date}
                  className={`rounded-lg overflow-hidden ${
                    isToday
                      ? `${accent.todayBg} border ${accent.todayBorder}`
                      : 'bg-slate-800/30'
                  }`}
                >
                  {/* Date Header */}
                  <div
                    className={`${sizes.headerPadding} flex items-center justify-between ${
                      isToday
                        ? `${accent.todayHeaderBg} border-b ${accent.todayHeaderBorder}`
                        : 'bg-slate-700/30 border-b border-slate-700/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`${sizes.dateText} font-semibold ${
                          isToday ? accent.todayText : 'text-slate-200'
                        }`}
                      >
                        {formatDateHeader(date)}
                      </span>
                      {isToday && (
                        <span className={`${sizes.dotSize} rounded-full ${accent.todayDot} animate-pulse`} />
                      )}
                    </div>
                    <span className={`${sizes.countText} text-slate-500`}>
                      {dateEvents.length} event{dateEvents.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Events for this date */}
                  <div className="divide-y divide-slate-700/30">
                    {visibleEvents.map((event) => (
                      <div
                        key={event.event_id}
                        className={`${sizes.eventPadding} hover:bg-slate-700/20 transition-colors`}
                      >
                        <div className="flex items-start gap-2">
                          {/* Country badge */}
                          {event.country && (
                            <span className="flex-shrink-0 px-1.5 py-0.5 bg-slate-700/50 rounded text-[9px] font-medium text-slate-400 uppercase">
                              {event.country}
                            </span>
                          )}

                          {/* Event name and values */}
                          <div className="flex-1 min-w-0">
                            <div className={`${sizes.eventText} text-slate-200 leading-tight`}>
                              {event.event_name}
                            </div>

                            {/* Values row - color coded */}
                            {(event.actual_value !== null ||
                              event.forecast_value !== null ||
                              event.previous_value !== null) && (() => {
                              const actualColors = getActualColor(event.actual_value, event.forecast_value);
                              return (
                              <div className="flex items-center gap-3 mt-1">
                                {event.actual_value !== null && (
                                  <div className="flex items-center gap-1">
                                    <span className={`${sizes.valueLabel} ${actualColors.label}`}>Act</span>
                                    <span className={`${sizes.valueText} font-semibold tabular-nums ${actualColors.value}`}>
                                      {formatValue(event.actual_value)}
                                    </span>
                                  </div>
                                )}
                                {event.forecast_value !== null && (
                                  <div className="flex items-center gap-1">
                                    <span className={`${sizes.valueLabel} text-amber-400/70`}>Fcst</span>
                                    <span className={`${sizes.valueText} text-amber-400 font-medium tabular-nums`}>
                                      {formatValue(event.forecast_value)}
                                    </span>
                                  </div>
                                )}
                                {event.previous_value !== null && (
                                  <div className="flex items-center gap-1">
                                    <span className={`${sizes.valueLabel} text-slate-500`}>Prev</span>
                                    <span className={`${sizes.valueText} text-slate-300 tabular-nums`}>
                                      {formatValue(event.previous_value)}
                                    </span>
                                  </div>
                                )}
                              </div>
                              );
                            })()}
                          </div>

                          {/* Arrow indicator for events without data */}
                          {event.actual_value === null &&
                            event.forecast_value === null && (
                              <ChevronRight className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-slate-600 flex-shrink-0 mt-0.5`} />
                            )}
                        </div>
                      </div>
                    ))}
                    {hiddenCount > 0 && (
                      <div className={`${sizes.eventPadding} ${sizes.countText} text-slate-500 text-center`}>
                        +{hiddenCount} more event{hiddenCount !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with total count */}
      <div className={`pt-2 mt-2 border-t border-slate-700/30 flex-shrink-0`}>
        <div className={`${sizes.footerText} text-slate-500 text-center`}>
          {totalEvents} {viewMode === 'upcoming' ? 'upcoming' : 'recent'} events
        </div>
      </div>
    </div>
  );
};

export default EconomicCalendarView;
